import axios from "axios";
import {
    isArray,
    isObject,
    mapValues
} from "lodash";
import i18n from 'i18next';
import { store } from "./Store";
import { SetTokenExpiresAt } from "./Actions/User";
import GetCookie from './Components/Common/Functions/GetCookie';
import { Rule } from "@mui/icons-material";
import {
    filterVersionsAndVariants
} from "./Components/ItineraryType/utils/filterVersionsAndVariants";
import { AppState } from "./Reducers/Reducers";

// ─── Token expiry tracking ────────────────────────────────────────────────────
// Capture token_expires_at / expires_at from API responses and dispatch to Redux
// so UI components (e.g. expiry warnings) can react to it.
const scheduleExpiryWarning = (expiresAt: string) => {
    store.dispatch(SetTokenExpiresAt(expiresAt));
};

// Clear the token cookie (domain-aware, matching SetCookie convention).
const clearTokenCookie = () => {
    const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    const host = window.location.host;
    const domainPart = host === "localhost:3000" ? "" : `;Domain=${host}`;
    document.cookie = `token=;${expires};path=/${domainPart}`;
};

axios.interceptors.request.use(
    (request) => {
        if (request.url) {
            try {
                const url = new URL(
                    request.url,
                    !request.baseURL && !request.url.startsWith('http') ?
                        window.location.origin :
                        request.baseURL
                );
                if (
                    url.pathname.endsWith('/flight/') ||
                    url.pathname.endsWith('/accommodation/') ||
                    url.pathname.endsWith('/accommodations/') ||
                    url.pathname.endsWith('/transfers/') ||
                    url.pathname.endsWith('/manual-products/') ||
                    url.pathname.endsWith('/custom-products/') ||
                    url.pathname.endsWith('/destinations/') ||
                    url.pathname.endsWith('/children/') ||
                    url.pathname.endsWith('/instances/') ||
                    url.pathname.endsWith('/custom-providers/') ||
                    url.pathname.endsWith('/circuit-category/') ||
                    url.pathname.endsWith('/auth/')
                ) {
                    request.url = request.url.replace('/v1', '/v2');
                }

                if (url.pathname.includes('/user/') && !url.pathname.includes('/crm')) {
                    request.params = {
                        ...request.params ?? {},
                        general_info: true
                    }
                }
            } catch (error) {
                if (!(error instanceof Error) || !error.message.includes('Invalid URL')) {
                    console.error(error);
                }
            }
        }

        if (window.location.pathname.includes('/provider-token')) {
            const matches = /\/provider-token\/([^\/]+)/.exec(window.location.pathname);
            const token = matches?.[1];
            if (token) {
                return {
                    ...request,
                    params: {
                        ...request.params,
                        provider_token: token
                    }
                };
            }
        }

        const requestUrl = request.url ?? '';
        const isApiRequest = requestUrl.includes(API_HREF) || requestUrl.includes(API_HREF.replace("v1", "v2"));

        if (!isApiRequest) {
            return request;
        }

        if (!request.headers['Accept']) {
            request.headers['Accept'] = 'application/json';
        }

        if (!request.headers['Content-Type']) {
            request.headers['Content-Type'] = 'application/json';
        }
        request.headers['X-ENVIRONMENT'] = 'APPS';
        request.headers['Accept-Language'] = i18n.language;

        if (request.headers['X-Public-Request']) {
            delete request.headers['X-Public-Request'];
            return request;
        }

        const token = GetCookie("token");
        let url = request.url ?? '';
        url = url.startsWith('http') ? url :
            window.location.origin + url;
        if (
            token &&
            !request.headers['Authorization'] &&
            (
                !url ||
                !(new URL(url).pathname.endsWith('/instances/'))
            )
        ) {
            request.headers['Authorization'] = `Token ${token}`;
        }

        return request;
    },
    (error) => {
        return Promise.reject(error);
    },
    {
        synchronous: true
    }
);

function replaceUrlsWithCompressedPicture(data: unknown): unknown {
    if (isArray(data)) {
        return data.map((item) => {
            return replaceUrlsWithCompressedPicture(item);
        });
    }

    if (isObject(data)) {
        if (
            'compressed_picture' in data &&
            data.compressed_picture
        ) {
            if ('url' in data) {
                data.url = data.compressed_picture;
            }

            if ('thumbnail_big' in data) {
                data.thumbnail_big = data.compressed_picture;
            }

            if ('thumbnail_medium' in data) {
                data.thumbnail_medium = data.compressed_picture;
            }
        }

        return mapValues(
            data,
            (value) => replaceUrlsWithCompressedPicture(value)
        );
    }

    return data;
}

axios.interceptors.response.use(
    (response) => {
        const data = response.data;
        if (data && typeof data === 'object' && !(data instanceof Blob)) {
            if (data.token_expires_at) {
                scheduleExpiryWarning(data.token_expires_at);
            }
            if (data.expires_at && data.token) {
                scheduleExpiryWarning(data.expires_at);
            }
        }
        const request = response.config;

        if (response.data && !(response.data instanceof Blob)) {
            if (request.params?.frontFilterVersionsAndVariants) {
                return {
                    ...response,
                    data: replaceUrlsWithCompressedPicture(
                        filterVersionsAndVariants({
                            rootData: response.data,
                            user: (store.getState() as AppState).user.user
                        })
                    )
                }
            }
            return {
                ...response,
                data: replaceUrlsWithCompressedPicture(response.data)
            };
        }

        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            clearTokenCookie();
            store.dispatch(SetTokenExpiresAt(null));
            window.location.href = `${WP_BASE_HREF}home`;
        }
        return Promise.reject(error);
    }
);
